/**
 * Fits a driving cab pass card into ISO ID-1 (credit card) proportions for on-screen preview and print.
 * Scales with transform-origin top-left + pixel offsets (not left:50% + translate(-50%)) so iOS
 * Safari does not expand ancestor scrollWidth and drift the page left/right after remounts.
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { CAB_PASS_CARD_HEIGHT_MM, CAB_PASS_CARD_WIDTH_MM, DrivingCabPassCard } from "./DrivingCabPassCard";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeCabPassPrintDomId(passId: string): string {
  return String(passId ?? "").replace(/[^a-zA-Z0-9_-]/g, "_");
}

/** ISO ID-1 card dimensions (landscape). */
const CARD_W_MM = "85.6mm";
const CARD_H_MM = "53.98mm";

/** A4 print sheet with cut guides, instructions, and duplex-aligned front/back pages. */
const CAB_PASS_CREDIT_CARD_PRINT_CSS = `
@page { size: A4 portrait; margin: 12mm; }
html, body { margin: 0; padding: 0; background: #fff; }
body {
  font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
  color: #0f172a;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.cab-pass-print-page {
  box-sizing: border-box;
  min-height: calc(297mm - 24mm);
  display: flex;
  flex-direction: column;
  page-break-after: always;
  break-after: page;
}
.cab-pass-print-page:last-child {
  page-break-after: auto;
  break-after: auto;
}
.cab-pass-print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10mm;
  margin-bottom: 8mm;
  padding-bottom: 4mm;
  border-bottom: 1px solid #cbd5e1;
}
.cab-pass-print-instructions { flex: 1; min-width: 0; }
.cab-pass-print-instructions h1 {
  margin: 0 0 2.5mm;
  font-size: 13pt;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.cab-pass-print-instructions p {
  margin: 0 0 2mm;
  font-size: 9.5pt;
  line-height: 1.45;
  color: #334155;
}
.cab-pass-print-instructions ol {
  margin: 2mm 0 0;
  padding-left: 5mm;
  font-size: 9pt;
  line-height: 1.4;
  color: #475569;
}
.cab-pass-print-instructions li { margin-bottom: 1mm; }
.cab-pass-print-logo {
  flex-shrink: 0;
  width: 32mm;
  height: auto;
  max-height: 14mm;
  object-fit: contain;
  object-position: top right;
}
.cab-pass-print-card-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6mm 4mm 10mm;
}
.cab-pass-cut-zone {
  position: relative;
  box-sizing: content-box;
  width: ${CARD_W_MM};
  height: ${CARD_H_MM};
  padding: 4mm;
  border: 1.5px dashed #64748b;
  border-radius: 2mm;
  background: #f8fafc;
}
.cab-pass-cut-label {
  position: absolute;
  top: -5mm;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  padding: 0.5mm 2.5mm;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  background: #fff;
  white-space: nowrap;
}
.cab-pass-scissors {
  position: absolute;
  font-size: 13pt;
  line-height: 1;
  color: #64748b;
  user-select: none;
}
.cab-pass-scissors-tl { top: -2mm; left: -1mm; transform: rotate(-50deg); }
.cab-pass-scissors-tr { top: -2mm; right: -1mm; transform: rotate(40deg) scaleX(-1); }
.cab-pass-scissors-bl { bottom: -2mm; left: -1mm; transform: rotate(50deg) scaleX(-1); }
.cab-pass-scissors-br { bottom: -2mm; right: -1mm; transform: rotate(-40deg); }
.cab-pass-card-slot {
  width: ${CARD_W_MM};
  height: ${CARD_H_MM};
  position: relative;
  overflow: hidden;
  border-radius: 0.8mm;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
}
.cab-pass-card-slot > * {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
}
.cab-pass-page-footer {
  margin-top: auto;
  padding-top: 4mm;
  text-align: center;
  font-size: 8.5pt;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}
.cab-pass-print-page--back .cab-pass-card-slot {
  transform: rotate(180deg);
}
style { display: none !important; }
`;

function escapeHtml(raw: string): string {
  return String(raw ?? "").replace(/[<>&"]/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] ?? c
  );
}

function extractCabPassFaceHtml(inner: Element, selector: string): string | null {
  const el = inner.querySelector(selector);
  if (!el) return null;
  return el.outerHTML;
}

function railIntelLogoUrl(): string {
  return new URL("/rail-intel-logo-light.png", window.location.origin).href;
}

function buildCabPassPrintSheetHtml(frontHtml: string, backHtml: string | null, documentTitle: string): string {
  const logo = escapeHtml(railIntelLogoUrl());
  const title = escapeHtml(documentTitle);

  const cutZone = (faceHtml: string) => `
<div class="cab-pass-cut-zone">
  <p class="cab-pass-cut-label">Cut along dotted line</p>
  <span class="cab-pass-scissors cab-pass-scissors-tl" aria-hidden="true">&#9986;</span>
  <span class="cab-pass-scissors cab-pass-scissors-tr" aria-hidden="true">&#9986;</span>
  <span class="cab-pass-scissors cab-pass-scissors-bl" aria-hidden="true">&#9986;</span>
  <span class="cab-pass-scissors cab-pass-scissors-br" aria-hidden="true">&#9986;</span>
  <div class="cab-pass-card-slot">${faceHtml}</div>
</div>`;

  const header = `
<div class="cab-pass-print-header">
  <div class="cab-pass-print-instructions">
    <h1>Driving cab pass — print &amp; cut</h1>
    <p>
      ISO ID-1 credit-card size (${CARD_W_MM}&nbsp;&times;&nbsp;${CARD_H_MM} landscape).
      Print <strong>both pages</strong> using your printer&rsquo;s <strong>double-sided</strong> option
      with <strong>Flip on long edge</strong> so the front and reverse align when cut out.
    </p>
    <ol>
      <li>Load paper and open print settings — choose <strong>Print on both sides</strong> / duplex.</li>
      <li>Select <strong>Flip on long edge</strong> (not short edge).</li>
      <li>Print page&nbsp;1 (front), then page&nbsp;2 (reverse) on the back of the same sheet.</li>
      <li>Cut along the dotted line. Optional: laminate for durability.</li>
    </ol>
  </div>
  <img class="cab-pass-print-logo" src="${logo}" alt="Rail Intel" />
</div>`;

  const frontPage = `
<div class="cab-pass-print-page cab-pass-print-page--front">
  ${header}
  <div class="cab-pass-print-card-area">
    ${cutZone(frontHtml)}
  </div>
  <p class="cab-pass-page-footer">Page 1 — Front</p>
</div>`;

  const backFace = backHtml ?? `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#fff;border:0.35mm solid rgba(0,0,0,.2);font-size:3mm;color:#64748b;text-align:center;padding:4mm;">Reverse not available for this preview.</div>`;

  const backPage = `
<div class="cab-pass-print-page cab-pass-print-page--back">
  ${header}
  <div class="cab-pass-print-card-area">
    ${cutZone(backFace)}
  </div>
  <p class="cab-pass-page-footer">Page 2 — Reverse (flip on long edge)</p>
</div>`;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${title}</title>${parentStylesheetLinks()}<style>${CAB_PASS_CREDIT_CARD_PRINT_CSS}</style></head><body>${frontPage}${backPage}</body></html>`;
}

function parentStylesheetLinks(): string {
  if (typeof document === "undefined") return "";
  return Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map((link) => {
      const href = link.getAttribute("href");
      if (!href) return "";
      const abs = link.href || new URL(href, window.location.href).href;
      return `<link rel="stylesheet" href="${abs.replace(/"/g, "&quot;")}">`;
    })
    .join("");
}

/** ISO ID-1 credit-card print via hidden iframe (A4 sheet, front + duplex-aligned reverse). */
export function printCabPassCreditCard(passId: string, documentTitle?: string): boolean {
  if (typeof document === "undefined") return false;
  const safeId = safeCabPassPrintDomId(passId);
  const root = document.getElementById(`cab-pass-print-root-${safeId}`);
  const inner = root?.querySelector(".cab-pass-scale-inner");
  if (!inner) return false;

  const frontHtml =
    extractCabPassFaceHtml(inner, ".cab-pass-flip-face-front") ??
    extractCabPassFaceHtml(inner, ".cab-pass-static-front") ??
    (() => {
      const wrap = inner.querySelector(".cab-pass-flip-inner > div:first-child");
      const face = wrap?.querySelector(".cab-pass-flip-face-front");
      return face ? face.outerHTML : wrap?.innerHTML ?? null;
    })();
  if (!frontHtml) return false;

  const backHtml =
    extractCabPassFaceHtml(inner, ".cab-pass-flip-face-back") ??
    (() => {
      const wrap = inner.querySelector(".cab-pass-flip-inner > div:last-child");
      const face = wrap?.querySelector(".cab-pass-flip-face-back");
      return face ? face.outerHTML : wrap?.innerHTML ?? null;
    })();

  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    iframe.remove();
    return false;
  }

  const title = String(documentTitle ?? "Driving cab pass");
  iframeDoc.open();
  iframeDoc.write(buildCabPassPrintSheetHtml(frontHtml, backHtml, title));
  iframeDoc.close();

  const cleanup = () => {
    iframe.remove();
  };

  window.setTimeout(() => {
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      cleanup();
      return;
    }
    const onAfter = () => {
      cleanup();
      printWindow.removeEventListener("afterprint", onAfter);
    };
    printWindow.addEventListener("afterprint", onAfter);
    printWindow.focus();
    printWindow.print();
    window.setTimeout(cleanup, 2000);
  }, 350);

  return true;
}

function withId1Layout(children: React.ReactNode): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (child.type === DrivingCabPassCard) {
      return React.cloneElement(child as React.ReactElement<{ id1Layout?: boolean }>, { id1Layout: true });
    }
    return child;
  });
}

/** Cached ISO ID-1 size in px — avoids briefly scaling the card to 1× during measure (that widened the page on mobile). */
let cabPassCardPxCache: { w: number; h: number } | null = null;

function getCabPassCardIntrinsicPxSize(): { w: number; h: number } {
  if (cabPassCardPxCache) return cabPassCardPxCache;
  if (typeof document === "undefined") {
    return { w: 323, h: 204 };
  }
  const probe = document.createElement("div");
  probe.style.cssText = `position:fixed;left:-9999px;top:0;visibility:hidden;pointer-events:none;width:${CAB_PASS_CARD_WIDTH_MM};height:${CAB_PASS_CARD_HEIGHT_MM};`;
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  cabPassCardPxCache = { w: Math.max(rect.width, 1), h: Math.max(rect.height, 1) };
  document.body.removeChild(probe);
  return cabPassCardPxCache;
}

export function CabPassScaledCardPreview({
  passId,
  children,
  className,
  /** Bump when nested card media changes so scaled/composited layers repaint on mobile Safari. */
  paintKey,
}: {
  passId: string;
  children: React.ReactNode;
  className?: string;
  paintKey?: string;
}) {
  const safeId = safeCabPassPrintDomId(passId);
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ scale: 0.2, ox: 0, oy: 0 });

  const recompute = useCallback(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const ow = outer.clientWidth;
    const oh = outer.clientHeight;
    if (ow < 1 || oh < 1) return;

    const { w: iw, h: ih } = getCabPassCardIntrinsicPxSize();
    const nextScale = Math.min(ow / iw, oh / ih, 1);
    const ox = Math.max(0, (ow - iw * nextScale) / 2);
    const oy = Math.max(0, (oh - ih * nextScale) / 2);
    setLayout((prev) => {
      if (
        Math.abs(prev.scale - nextScale) < 1e-4 &&
        Math.abs(prev.ox - ox) < 0.5 &&
        Math.abs(prev.oy - oy) < 0.5
      ) {
        return prev;
      }
      return { scale: nextScale, ox, oy };
    });
  }, []);

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(() => recompute());
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    const onViewport = () => {
      requestAnimationFrame(() => recompute());
    };
    window.addEventListener("resize", onViewport);
    window.addEventListener("orientationchange", onViewport);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onViewport);
      window.removeEventListener("orientationchange", onViewport);
    };
  }, [recompute, paintKey]);

  /** Invalidate compositor subtree after blob/data-URL swaps (Safari ignores until scroll unless we poke). */
  useLayoutEffect(() => {
    if (!paintKey) return;
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    void outer.getBoundingClientRect();
    inner.style.opacity = "0.999";
    requestAnimationFrame(() => {
      inner.style.opacity = "";
      void inner.getBoundingClientRect();
      recompute();
    });
  }, [paintKey, recompute]);

  // top-left origin + pixel translate keeps pre-transform layout from starting at 50% width
  // (left:50% + translate(-50%) was expanding iOS scrollWidth and drifting the page).
  const scaledTransform = `translate(${layout.ox}px, ${layout.oy}px) scale(${layout.scale})`;

  return (
    <div
      id={`cab-pass-print-root-${safeId}`}
      ref={outerRef}
      className={cn(
        "relative mx-auto overflow-hidden rounded-md border-2 border-slate-400 bg-[#f8fafc] shadow-md",
        "w-full max-w-full min-w-0 [aspect-ratio:85.6/53.98] sm:max-w-[min(100%,22rem)]",
        "[contain:layout_paint] [overflow-x:clip] [overflow-y:clip]",
        className
      )}
    >
      <div className="cab-pass-scale-wrap absolute inset-0 overflow-hidden [contain:strict] [overflow-x:clip] [overflow-y:clip]">
        <div
          ref={innerRef}
          className="cab-pass-scale-inner absolute left-0 top-0 shrink-0"
          style={{
            width: CAB_PASS_CARD_WIDTH_MM,
            minWidth: CAB_PASS_CARD_WIDTH_MM,
            height: CAB_PASS_CARD_HEIGHT_MM,
            minHeight: CAB_PASS_CARD_HEIGHT_MM,
            transform: scaledTransform,
            transformOrigin: "top left",
            WebkitTransform: scaledTransform,
            WebkitTransformOrigin: "top left",
            willChange: "transform",
          }}
        >
          {withId1Layout(children)}
        </div>
      </div>
    </div>
  );
}
